/**
 * @lines98/client/lib/replay-save-button — ОБЩИЙ kit «дискета-сохранить» на просмотре
 * ЧУЖОГО реплея (тикет B, frontend-lead 2026-06-24). Один источник логики «сохранить /
 * убрать / овершлоу / гость→логин» для ВСЕХ 4 чужих-surface'ов (плеер чужого playable,
 * тизер чужой daily, страница `/r/<short_id>`, чужой реплей из лидерборда) — экраны
 * монтируют ЭТОТ kit, НЕ копируют book-API/anchored-popover-вызовы (AC-B10 / §модульность).
 *
 * Модель «ствол → ветки»: kit строит icon-button-дискету (`components/icon-button` +
 * глиф `ICON_SAVE` из единого реестра), держит состояние (не сохранено / в Книге),
 * проводит сохранение/удаление через сервисный слой `services/book-api` (POST с
 * idempotency-key из `lib/idempotency-key`; DELETE), и при 409 `album_full` показывает
 * овершлоу-попап на ТОМ ЖЕ
 * примитиве `lib/anchored-popover`, что info-popover/achievement-detail (постоянный
 * оверлей — там anchored уместен) — текст РОВНО `t('book.overflow')` (owner-lock).
 *
 * ВЫБОР ПРИМИТИВА ПО ТИПУ ФИДБЭКА (§2.1 пред-флайт, owner-directive 2026-06-24):
 *   · ПОСТОЯННЫЙ оверлей (овершлоу 5/5, гость-промпт) → `anchored-popover` (анкер у кнопки);
 *   · TRANSIENT-фидбэк («Ссылка скопирована») → ЗАТУХАЮЩИЙ ТОСТ `ui/toast` (showToast),
 *     НЕ anchored-popover (плашка с крестиком для «скопировано» = owner «уродство»). Плюс
 *     сама share-кнопка РЕАГИРУЕТ на нажатие (success-морф глиф→✓, см. attachReplayShareButton).
 *
 * РАЗМЕЩЕНИЕ (AC-B5, owner-lock 320×480): дискета — icon-button РАЗМЕРА транспорт-кнопки
 * (`--btn-h`), но монтируется ВНЕ 2×3-сетки `.replay-transport` (7-й контрол ломал 2D-
 * раскладку — инцидент «Дуэль» 2026-06-21). Хост (remote-replay) кладёт `.element` в
 * угол-шапку плеера (`.replay-save-slot`), где рамка дискеты не пересекается с сеткой
 * транспорта/доской на свипе ширин 300..420. Дискета наследует единый закон заполнения
 * глифа (`ACTION_RAIL_FILL`) — тот же модуль рейл-кнопок, не своя пропорция.
 *
 * РЕШЕНИЕ «чужой/свой» — СЕРВЕРНОЕ (хост передаёт `kind`/owner-флаг из remote-replay flow);
 * kit НЕ гадает «if mine»/«if today». На своём реплее kit НЕ монтируется вовсе (AC-B1).
 *
 * Cross-references: тикет B AC-B1..B10; спека §2.5/§2.7/§5.3/§6.2/§6.3; ADR-0023 §POST/
 *   DELETE/овершлоу-контракт/own_game_not_bookmarkable; components/icon-button (ICON_SAVE);
 *   lib/anchored-popover (овершлоу-ствол); services/book-api; lib/action-rail (ACTION_RAIL_FILL).
 */

import '../styles/replay-save-button.css';

import { createIconButton, ICON_CHECK, ICON_CLOSE, ICON_SAVE, ICON_SHARE } from '../components/icon-button.js';
import { ACTION_RAIL_FILL } from './action-rail.js';
import { mintIdempotencyKey } from './idempotency-key.js';
import { t } from './i18n/index.js';
import { copyShareLink } from './share-link.js';
import { saveOthersBookmark } from '../services/book-api.js';
import { showToast } from '../ui/toast.js';

/**
 * Длительность success-морфа share-кнопки (мс): глиф кратко меняется на «✓» +
 * accent-подсветка (data-copied), потом возвращается к ICON_SHARE. Best-practice
 * copy-кнопок — мгновенная видимая реакция на нажатие (owner-directive 2026-06-24).
 * 1400мс ≈ время чтения тоста (showToast default 1600) → кнопка «остывает» вместе с
 * фидбэком; не зашитый в логику magic — единственная ручка тайминга реакции кнопки.
 */
const SHARE_COPIED_FEEDBACK_MS = 1400;

/**
 * Закон заполнения глифа дискеты = единая ручка рейл-кнопок (`ACTION_RAIL_FILL`) — НЕ
 * своя пропорция (один источник, action-rail). Доля размера icon-button-бокса под SVG
 * (CSS `--save-glyph-fill`); читается replay-save-button.css. Reference на kit рейла
 * держит дисциплину «дискета — контрол того же рейла транспорта» (AC-B2 reuse).
 */
const SAVE_GLYPH_FILL = ACTION_RAIL_FILL;

export interface ReplaySaveButtonOptions {
  /** short_id чужой партии — натур-ключ закладки (ADR-0023). */
  readonly shortId: string;
  /** Уже в Книге? (server-truth из открытия реплея) — стартовое состояние «в Книге». */
  readonly savedInitially?: boolean;
  /**
   * Гость? Если true — клик ведёт на логин (через `onRequestLogin`), сохранение НЕ
   * выполняется (AC-B6). Решение «гость» — серверное (isAuthed), хост передаёт.
   */
  readonly isGuest?: boolean;
  /** Открыть поверхность логина (auth-modal) — хост прокидывает общий auth-flow. */
  readonly onRequestLogin: () => void;
}

export interface ReplaySaveButtonHandle {
  /** Кнопка-дискета (icon-button ≥44×44) — хост вставляет в шапку плеера. */
  readonly element: HTMLButtonElement;
  /** Снять попап + слушатели (на unmount экрана). */
  destroy(): void;
}

/**
 * Создать kit-дискету для чужого реплея. Возвращает `{ element, destroy }`. Хост монтирует
 * `element` ВНЕ сетки транспорта (AC-B5). Сам kit владеет состоянием + овершлоу-попапом.
 */
export function attachReplaySaveButton(opts: ReplaySaveButtonOptions): ReplaySaveButtonHandle {
  let saved = opts.savedInitially === true;
  let busy = false;

  // Дискета — примитив icon-button (тактильные 44px + центрирование зашиты ВНУТРЬ).
  // aria/label из каталога (AC-B8); состояние «в Книге» — data-saved (ОДИН visual marker,
  // AC-B3: галка-наложение рисует CSS, не второй глиф). Глиф-fill — единая ручка рейла.
  const btn = createIconButton({
    glyph: ICON_SAVE,
    label: saved ? t('book.save.saved') : t('book.save.label'),
    variant: 'framed',
    className: 'replay-save-btn',
  }).element;
  btn.style.setProperty('--save-glyph-fill', String(SAVE_GLYPH_FILL));

  // anchored-popover'ы овершлоу/гость-промпта УДАЛЕНЫ (owner-directive 2026-06-25: ВСЕ
  // исходы дискеты — затухающий тост + ✗/✓-реакция кнопки, БЕЗ плашки-попапа; как у share).
  // Тексты идут в showToast: успех → book.saveSuccess, гость → book.savePrompt, овершлоу →
  // book.overflow. Один примитив фидбэка (ui/toast) на оба контрола рейла.

  /**
   * Применить визуальное состояние дискеты (один marker — data-saved).
   *
   * SAVED (уже в Архиве) → СЕРАЯ + НЕАКТИВНАЯ (owner-directive 2026-06-25): снятие записи
   * теперь ТОЛЬКО из таблицы Архива (вкладка «Чужие»), поэтому на просмотре чужого реплея
   * сохранённая дискета — это СТАТУС «уже сохранено» (label «В архиве»), а НЕ тоггл-снятие.
   * data-saved='true' рисует приглушённый disabled-вид (CSS), `disabled` + `aria-disabled`
   * → клик не снимает, pointer-events отключены. (Прежняя accent-заливка+toggle убраны.)
   * UNSAVED → активная контурная дискета «Сохранить в архив» (можно сохранить).
   */
  const applyState = (): void => {
    if (saved) {
      btn.dataset.saved = 'true';
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-label', t('book.save.saved'));
      // НЕ aria-pressed: это статус «в архиве», не переключатель (снятие — из таблицы Архива).
      btn.removeAttribute('aria-pressed');
    } else {
      delete btn.dataset.saved;
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.setAttribute('aria-label', t('book.save.label'));
      btn.removeAttribute('aria-pressed');
    }
  };
  applyState();

  // РЕАКЦИЯ ДИСКЕТЫ НА ИСХОД (owner-directive 2026-06-25: «реакция на все варианты —
  // галочка при успехе, зачёркнутый знак при неудаче»). Кратковременный морф глифа:
  //   · успех → ✓ (ICON_CHECK) + data-flash='ok' (accent);
  //   · неудача (гость / архив полон / ошибка) → ✗ (ICON_CLOSE) + data-flash='fail' (--warn).
  // Через SHARE_COPIED_FEEDBACK_MS — возврат к ICON_SAVE + актуальному saved-стейту
  // (applyState: при успехе остаётся accent-заливка «в Книге»). Тот же приём, что
  // success-морф share-кнопки (flashCopied) — единый best-practice реакции кнопки (§2.1).
  let flashTimer: number | null = null;
  const flashResult = (ok: boolean): void => {
    btn.innerHTML = ok ? ICON_CHECK : ICON_CLOSE;
    btn.dataset.flash = ok ? 'ok' : 'fail';
    if (flashTimer !== null) window.clearTimeout(flashTimer);
    flashTimer = window.setTimeout(() => {
      flashTimer = null;
      btn.innerHTML = ICON_SAVE;
      delete btn.dataset.flash;
      applyState(); // вернуть глиф-наложение data-saved + aria по текущему состоянию
    }, SHARE_COPIED_FEEDBACK_MS);
  };

  const onClick = (): void => {
    if (busy) return;
    // Уже в Архиве → дискета СЕРАЯ/неактивная (disabled): снятие ТОЛЬКО из таблицы Архива
    // (owner-directive 2026-06-25). На просмотре клик по сохранённой дискете НЕ снимает —
    // backstop на случай программного клика мимо disabled.
    if (saved) return;
    // Гость → ЗАТУХАЮЩИЙ тост «Войдите…» + ✗-реакция кнопки (owner-directive 2026-06-25,
    // как у share — тост + реакция, без anchored-popover). Сохранение НЕ выполняется.
    if (opts.isGuest) {
      showToast(t('book.savePrompt'));
      flashResult(false);
      return;
    }
    void doSave();
  };

  async function doSave(): Promise<void> {
    busy = true;
    // Per-действие idempotency-nonce (ADR-0005 Option B): один логический клик «Сохранить»
    // = один ключ; транспортный ретрай (api-client re-mint) переиспользует тот же → эффект
    // один. book-api шлёт его заголовком X-Idempotency-Key.
    const res = await saveOthersBookmark(opts.shortId, mintIdempotencyKey());
    busy = false;
    if (res.ok) {
      // saved | already_saved — оба отражаем как «в Книге» (идемпотентно, AC-B3). Фидбэк:
      // затухающий тост «Сохранено в архив» (book.saveSuccess) + ✓-реакция дискеты
      // (flashResult(true) → потом остаётся accent-заливка saved). Best-practice: transient =
      // toast, кнопка реагирует (ADDENDUM-2/3, owner «с дискетой то же самое»).
      saved = true;
      applyState();
      showToast(t('book.saveSuccess'));
      flashResult(true);
      return;
    }
    if (res.reason === 'album_full') {
      // Овершлоу 5/5 → ЗАТУХАЮЩИЙ тост «Архив заполнен…» + ✗-реакция кнопки (owner-directive
      // 2026-06-25: тост + реакция, без уродской плашки). Закладка НЕ создана.
      showToast(t('book.overflow'));
      flashResult(false);
      return;
    }
    if (res.reason === 'unauthorized') {
      // Сессия отозвана/гость на сервере → логин (как nav/лидерборд) + ✗-реакция.
      flashResult(false);
      opts.onRequestLogin();
      return;
    }
    // own_game_not_bookmarkable / replay_not_found / network / server_error — backstop:
    // не сохранилось → ✗-реакция (без паники-плашки; «дискеты на своём» быть не должно).
    flashResult(false);
  }

  // doRemove УДАЛЁН (owner-directive 2026-06-25): снятие записи — ТОЛЬКО из таблицы Архива
  // (вкладка «Чужие», кнопка-перечёркнутая-дискета + confirm-dialog). На просмотре чужого
  // реплея сохранённая дискета лишь ПОКАЗЫВАЕТ статус «в архиве» (серая/disabled), не снимает.

  btn.addEventListener('click', onClick);

  return {
    element: btn,
    destroy(): void {
      btn.removeEventListener('click', onClick);
      if (flashTimer !== null) {
        window.clearTimeout(flashTimer);
        flashTimer = null;
      }
    },
  };
}

/**
 * Опции share-кнопки СВОЕГО реплея (реплей-под-волна AC-8/9/10). На СВОЁМ playable
 * реплее (server-truth `isOwn === true`) вместо дискеты «Сохранить» монтируется кнопка
 * «Поделиться» (ICON_SHARE): тап → копирует прямую ссылку `/r/<short_id>` в буфер через
 * ЕДИНЫЙ `lib/share-link` (НЕ свой clipboard) + ДВА канала фидбэка по best-practice
 * copy-кнопок (owner-directive 2026-06-24 «уродство… должно быть затухающее сообщение,
 * и кнопка сама должна реагировать на нажатие»):
 *   (1) ЗАТУХАЮЩИЙ ТОСТ «Ссылка скопирована» через ЕДИНЫЙ примитив `ui/toast` (showToast)
 *       — transient-фидбэк = тост, НЕ anchored-popover-плашка (§2.1 пред-флайт примитивов);
 *   (2) РЕАКЦИЯ КНОПКИ на нажатие — кратковременный success-морф: глиф → «✓» (ICON_CHECK)
 *       + accent-подсветка (data-copied), затем возврат (SHARE_COPIED_FEEDBACK_MS).
 * Свои партии авто-архивируются (без ручного сохранения), поэтому ручка тут = ШАРИНГ,
 * не save (owner: «терять свой реплей обидно; share — про виральность», концепт-меты §реплеи).
 */
export interface ReplayShareButtonOptions {
  /** short_id своей партии — для прямой ссылки `/r/<short_id>` (натур-ключ реплея). */
  readonly shortId: string;
}

/**
 * Создать share-kit для СВОЕГО реплея. Возвращает `{ element, destroy }` (тот же handle,
 * что у дискеты — хост монтирует `element` в тот же `#rp-save-slot`, ВНЕ сетки транспорта,
 * AC-12). Глиф/размер/fill — те же ручки (icon-button framed + ACTION_RAIL_FILL), чтобы
 * share-кнопка визуально = контрол того же рейла, что дискета.
 */
export function attachReplayShareButton(opts: ReplayShareButtonOptions): ReplaySaveButtonHandle {
  let busy = false;

  // Кнопка «Поделиться» — примитив icon-button (тактильные 44px + центрирование внутри).
  // Глиф ICON_SHARE из единого реестра (НЕ эмодзи). aria-label из каталога (AC-15).
  const btn = createIconButton({
    glyph: ICON_SHARE,
    label: t('book.share.label'),
    variant: 'framed',
    className: 'replay-share-btn',
  }).element;
  btn.style.setProperty('--save-glyph-fill', String(SAVE_GLYPH_FILL));

  // Таймер success-морфа (глиф→✓ + accent), чтобы повторный клик не накапливал откаты.
  let copiedTimer: number | null = null;

  /**
   * РЕАКЦИЯ КНОПКИ на успешное копирование (best-practice copy-кнопок): глиф кратко
   * становится «✓» (ICON_CHECK) + accent-подсветка (data-copied), `aria-label` отражает
   * успех для скринридера; через SHARE_COPIED_FEEDBACK_MS — возврат к ICON_SHARE. Морф
   * глифа — смена innerHTML примитива (а НЕ второй DOM-элемент): один контрол, один глиф.
   */
  const flashCopied = (): void => {
    btn.innerHTML = ICON_CHECK;
    btn.dataset.copied = 'true';
    btn.setAttribute('aria-label', t('book.shareCopied'));
    if (copiedTimer !== null) window.clearTimeout(copiedTimer);
    copiedTimer = window.setTimeout(() => {
      copiedTimer = null;
      btn.innerHTML = ICON_SHARE;
      delete btn.dataset.copied;
      btn.setAttribute('aria-label', t('book.share.label'));
    }, SHARE_COPIED_FEEDBACK_MS);
  };

  const onClick = (): void => {
    if (busy) return;
    busy = true;
    // Прямая ссылка `/r/<short_id>` через ЕДИНЫЙ lib/share-link (clipboard + fallback
    // инкапсулированы там; свой navigator.clipboard в ките = REJECT, AC-9). На успехе —
    // ЗАТУХАЮЩИЙ тост (showToast, transient-фидбэк = тост, §2.1) + реакция кнопки
    // (flashCopied). На отказе буфера — тихо (не паника); фидбэк только при ok.
    void copyShareLink(`/r/${opts.shortId}`).then((res) => {
      busy = false;
      if (res.ok) {
        showToast(t('book.shareCopied'));
        flashCopied();
      }
    });
  };
  btn.addEventListener('click', onClick);

  return {
    element: btn,
    destroy(): void {
      btn.removeEventListener('click', onClick);
      if (copiedTimer !== null) {
        window.clearTimeout(copiedTimer);
        copiedTimer = null;
      }
    },
  };
}
